import { capitalizeWords } from '@/lib/utils';
import type { UserDetails } from '@/types';
import type { CommentedBy } from '@/types/comments';

export type MentionUser = Pick<
  UserDetails | CommentedBy,
  'id' | 'firstName' | 'lastName' | 'username' | 'email'
>;

/**
 * Returns true when a name field is missing or a placeholder from the API.
 */
function isNullishNamePart(value?: string): boolean {
  if (!value) return true;
  const normalized = value.trim().toLowerCase();
  return normalized === '' || normalized === 'null' || normalized === '-';
}

/**
 * Escapes a string for use inside a RegExp.
 */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Display name for a mentioned user in plain-text previews (reply quotes, banners).
 */
export function getCommentUserDisplayName(user: MentionUser): string {
  const first = !isNullishNamePart(user.firstName) ? user.firstName.trim() : '';
  const last = !isNullishNamePart(user.lastName) ? user.lastName.trim() : '';
  const fullName = [first, last].filter(Boolean).join(' ');
  if (fullName) {
    return fullName;
  }

  const username = user.username?.trim();
  if (username && !username.includes('@')) {
    return username;
  }
  if (username?.includes('@')) {
    const usernameLocal = username.split('@')[0]?.trim();
    if (usernameLocal) {
      return usernameLocal;
    }
  }

  const emailLocal = user.email?.split('@')[0]?.trim();
  if (emailLocal) {
    return emailLocal;
  }

  return 'User';
}

/**
 * Capitalized display name for chat UI (message headers, mentions, autocomplete).
 */
export function getChatUserDisplayName(
  user: MentionUser | null | undefined,
  options?: { isMe?: boolean },
): string {
  if (!user) return '';
  const displayName = capitalizeWords(getCommentUserDisplayName(user));
  return options?.isMe ? `${displayName} (You)` : displayName;
}

/**
 * Collects mention labels that may appear after `@` in the composer for a member.
 */
function getMemberMentionLabels(member: MentionUser): string[] {
  const labels = new Set<string>();
  const displayName = getCommentUserDisplayName(member).trim();
  if (displayName) labels.add(displayName);

  const username = member.username?.trim();
  if (username) labels.add(username);

  const email = member.email?.trim();
  if (email) labels.add(email);

  return [...labels].sort((a, b) => b.length - a.length);
}

/**
 * Converts `@mention` labels in a draft message to `[user:id]` tokens before send.
 */
export function resolveDraftMentionsToUserTokens(
  message: string,
  members: MentionUser[],
): string {
  let resolvedMessage = message;

  const membersWithLabels = members
    .filter((member) => member.id != null)
    .map((member) => ({
      member,
      labels: getMemberMentionLabels(member),
    }))
    .sort((a, b) => {
      const longestA = a.labels[0]?.length ?? 0;
      const longestB = b.labels[0]?.length ?? 0;
      return longestB - longestA;
    });

  for (const { member, labels } of membersWithLabels) {
    for (const label of labels) {
      const escapedLabel = escapeRegExp(label);
      resolvedMessage = resolvedMessage.replace(
        new RegExp(`@${escapedLabel}(?=\\s|$|[.,!?;:])`, 'gi'),
        `[user:${member.id}]`,
      );
    }
  }

  return resolvedMessage;
}

/**
 * Returns true when a group member matches an `@` mention search query.
 */
export function memberMatchesMentionQuery(member: MentionUser, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  const searchableValues = [
    getCommentUserDisplayName(member),
    member.firstName,
    member.lastName,
    member.username,
    member.email,
  ]
    .filter((value): value is string => Boolean(value?.trim()))
    .map((value) => value.toLowerCase());

  return searchableValues.some(
    (value) => value.startsWith(normalizedQuery) || value.includes(normalizedQuery),
  );
}

/**
 * Replaces `[user:id]` tokens with `@DisplayName` for readable plain-text previews.
 */
export function resolveUserMentionsInCommentText(
  comment: string,
  users?: MentionUser[],
): string {
  if (!comment) return '';

  return comment.replace(/\[user:(\d+)\]/g, (token, userId) => {
    const user = users?.find((entry) => String(entry.id) === String(userId));
    if (!user) return token;
    return `@${getCommentUserDisplayName(user)}`;
  });
}

/**
 * Takes a comment and a list of users and returns structured data for rendering.
 * 
 * @returns Array of text and user objects for rendering
 */
export const getUserEmbeddedInComment = (
  comment: string,
  users: UserDetails[] | undefined
) => {
  const parts: Array<{ type: 'text' | 'user'; content?: string; user?: UserDetails }> = [];
  let lastIndex = 0;
  
  const regex = /\[user:(\d+)\]/g;
  let match;
  
  while ((match = regex.exec(comment)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({ 
        type: 'text', 
        content: comment.slice(lastIndex, match.index) 
      });
    }
    
    const userId = match[1];
    const user = users?.find(
      (entry) => entry != null && String(entry.id) === String(userId),
    );
    
    if (user) {
      parts.push({ type: 'user', user });
    } else {
      parts.push({ type: 'text', content: match[0] });
    }
    
    lastIndex = regex.lastIndex;
  }
  
  // Add remaining text
  if (lastIndex < comment.length) {
    parts.push({ 
      type: 'text', 
      content: comment.slice(lastIndex) 
    });
  }
  
  return parts;
};

/**
 * Returns true when a visual space should be inserted beside a mention
 * because the adjacent text segment has no whitespace on that edge.
 */
export function shouldPadMentionWithSpace(
  adjacentText: string | undefined,
  edge: 'before' | 'after',
): boolean {
  if (!adjacentText) {
    return false;
  }

  return edge === 'before' ? !/\s$/.test(adjacentText) : !/^\s/.test(adjacentText);
}
